/**
 * useSprintPosts — Firestore-direct subscription + CRUD for sprint posts at
 *   `projects/{pid}/content_posts/*` filtered by sprintId.
 *
 * Concurrent Minsky refactor (2026-04-29):
 *   Was: read sprints/{sid}/posts/* (path retired).
 *   Now: read content_posts WHERE sprintId == X. strategyId arg unused
 *   but kept in signature for backward-compat with existing callers.
 *
 * Three hooks shipped together (same file keeps the Firestore path helpers
 * colocated):
 *   - `useSprintPosts`   — list subscription for a single sprint
 *   - `useSprintPost`    — single-doc subscription
 *   - `useUpdateSprintPost` — CRUD helpers mirroring useProjectPostExamples
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection, doc, onSnapshot, updateDoc, deleteDoc, query, where,
  serverTimestamp,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { Post } from '../types';

function contentPostsColl(projectId: string) {
  const db = CoreFirebaseService.getInstance().firestore;
  return collection(db, 'projects', projectId, 'content_posts');
}

function contentPostRef(projectId: string, postId: string) {
  const db = CoreFirebaseService.getInstance().firestore;
  return doc(db, 'projects', projectId, 'content_posts', postId);
}

function stripUndefined<T>(input: T): T {
  if (input === null || typeof input !== 'object') return input;
  if (Array.isArray(input)) {
    return input.map((v) => stripUndefined(v)) as unknown as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = typeof v === 'object' && v !== null ? stripUndefined(v) : v;
  }
  return out as T;
}

// ─────────────────────────────────────────────────────────────
// Subscriptions
// ─────────────────────────────────────────────────────────────

export interface UseSprintPostsReturn {
  posts: Post[];
  loading: boolean;
  error: Error | null;
}

export function useSprintPosts(
  projectId: string | null,
  _strategyId: string | null,
  sprintId: string | null,
  authorId?: string | null,
): UseSprintPostsReturn {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !sprintId || authorId === 'all') {
      setPosts([]); setLoading(false); return;
    }
    setLoading(true);
    // Order is now driven by ContentPublication.scheduledAt at the consumer
    // join layer. Here we just subscribe to the filtered set; Calendar joins
    // separately. (Sort fallback: createdAt is monotonic per generation run.)
    const q = authorId
      ? query(contentPostsColl(projectId), where('authorId', '==', authorId), where('sprintId', '==', sprintId))
      : query(contentPostsColl(projectId), where('sprintId', '==', sprintId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Post)));
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId, sprintId, authorId]);

  return { posts, loading, error };
}

export function useSprintPost(
  projectId: string | null,
  _strategyId: string | null,
  _sprintId: string | null,
  postId: string | null,
): { post: Post | null; loading: boolean; error: Error | null } {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !postId) {
      setPost(null); setLoading(false); return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      contentPostRef(projectId, postId),
      (snap) => {
        setPost(snap.exists() ? ({ id: snap.id, ...snap.data() } as Post) : null);
        setLoading(false);
      },
      (err) => { setError(err as Error); setLoading(false); },
    );
    return () => unsub();
  }, [projectId, postId]);

  return { post, loading, error };
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

export interface UseUpdateSprintPostReturn {
  updatePost: (postId: string, patch: Partial<Post>) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

export function useUpdateSprintPost(
  projectId: string | null,
  _strategyId: string | null,
  _sprintId: string | null,
): UseUpdateSprintPostReturn {
  const updatePost = useCallback(async (postId: string, patch: Partial<Post>) => {
    if (!projectId) throw new Error('projectId required');
    const ref = contentPostRef(projectId, postId);
    const payload = stripUndefined({
      ...patch,
      updatedAt: serverTimestamp(),
    });
    await updateDoc(ref, payload as any);
  }, [projectId]);

  const deletePost = useCallback(async (postId: string) => {
    if (!projectId) return;
    await deleteDoc(contentPostRef(projectId, postId));
  }, [projectId]);

  return { updatePost, deletePost };
}

// ─────────────────────────────────────────────────────────────
// Memoized derived — useful when a consumer already subscribed to a
// full strategy's posts and wants the per-sprint slice.
// ─────────────────────────────────────────────────────────────

export function useSprintPostsSlice(
  allPosts: Post[] | undefined,
  sprintId: string | null,
): Post[] {
  return useMemo(() => {
    if (!allPosts || !sprintId) return [];
    return allPosts.filter((p) => p.sprintId === sprintId);
  }, [allPosts, sprintId]);
}
